import { supabase } from './client';
const toNumber = (v) => v === null || v === undefined ? undefined : Number(v);
const clientFromRow = (row) => ({
    id: row.id,
    firstName: row.first_name ?? undefined,
    lastName: row.last_name ?? undefined,
    email: row.email ?? undefined,
    contact: row.contact ?? undefined,
    rijksregisterMoeder: row.rijksregister_moeder ?? undefined,
});
const patientFromRow = (row) => ({
    id: row.id,
    clientId: row.client_id,
    firstName: row.first_name,
    lastName: row.last_name,
    rijksregister: row.rijksregister ?? undefined,
    mutualiteit: row.mutualiteit ?? undefined,
});
const caseFromRow = (row) => ({
    id: row.id,
    patientId: row.patient_id,
    stage: (row.stage ?? undefined),
    status: row.status ?? undefined,
    status1: row.status1 ?? undefined,
    deadline: row.deadline ?? undefined,
    submittedOn: row.submitted_on ?? undefined,
    received: row.received ?? undefined,
    amount: toNumber(row.amount),
    paid: toNumber(row.paid),
    retroactive: toNumber(row.retroactive),
    monthly: toNumber(row.monthly),
    tags: row.tags ?? undefined,
});
const caseToRow = (data) => ({
    patient_id: data.patientId,
    stage: data.stage,
    status: data.status,
    status1: data.status1,
    deadline: data.deadline,
    submitted_on: data.submittedOn,
    received: data.received,
    amount: data.amount,
    paid: data.paid,
    retroactive: data.retroactive,
    monthly: data.monthly,
    tags: data.tags,
});
const patientToRow = (data) => ({
    client_id: data.clientId,
    first_name: data.firstName,
    last_name: data.lastName,
    rijksregister: data.rijksregister,
    mutualiteit: data.mutualiteit,
});
const clientToRow = (data) => ({
    first_name: data.firstName,
    last_name: data.lastName,
    email: data.email,
    contact: data.contact,
    rijksregister_moeder: data.rijksregisterMoeder,
});
const caseWithRelationsFromRow = (row) => {
    const { patients, ...caseRow } = row;
    const { clients, ...patientRow } = patients;
    return {
        ...caseFromRow(caseRow),
        patient: patientFromRow(patientRow),
        client: clientFromRow(clients),
    };
};
export const getAllCases = async () => {
    const { data, error } = await supabase
        .from('cases')
        .select('*, patients(*, clients(*))');
    if (error)
        throw error;
    return { cases: (data ?? []).map(caseWithRelationsFromRow), status: 200 };
};
export const getCaseById = async (id) => {
    const { data, error } = await supabase
        .from('cases')
        .select('*, patients(*, clients(*))')
        .eq('id', id)
        .maybeSingle();
    if (error)
        throw error;
    return data ? caseWithRelationsFromRow(data) : null;
};
export const createClient = async (data) => {
    const { data: inserted, error } = await supabase
        .from('clients')
        .insert(clientToRow(data))
        .select()
        .single();
    if (error)
        throw error;
    return clientFromRow(inserted);
};
export const createPatient = async (data) => {
    const { data: inserted, error } = await supabase
        .from('patients')
        .insert(patientToRow(data))
        .select()
        .single();
    if (error)
        throw error;
    return patientFromRow(inserted);
};
export const createCase = async (data) => {
    const { data: inserted, error } = await supabase
        .from('cases')
        .insert(caseToRow(data))
        .select()
        .single();
    if (error)
        throw error;
    return { ...caseFromRow(inserted), status: 200 };
};
/**
 * Creates a new case for a brand-new patient, auto-creating a (likely blank)
 * client to own that patient, matching the "placeholder client" convention
 * used throughout the existing data.
 */
export const createCaseWithNewPatient = async (clientData, patientData, caseData) => {
    const client = await createClient(clientData);
    const patient = await createPatient({ ...patientData, clientId: client.id });
    return createCase({ ...caseData, patientId: patient.id });
};
export const updateClient = async (id, data) => {
    const { error } = await supabase.from('clients').update(clientToRow(data)).eq('id', id);
    if (error)
        throw error;
};
export const updatePatient = async (id, data) => {
    const { error } = await supabase.from('patients').update(patientToRow(data)).eq('id', id);
    if (error)
        throw error;
};
